import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../Navbar";
import ProBlogCard from "./ProBlogCard";
import {
  getProBlog,
  payForCreatorAccess,
  formatDate,
  formatRemaining,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// ONE PRO BLOG — AND THE SECURE SHARE PREVIEW  (Steps 8, 12, 14)
//
// This is the page a share link opens. Step 14 asks for
//   Share Link → Preview → Paid? → Yes → Full Blog
//                                ↳ No → Pay → Unlock
// and that is exactly what happens — but the decision is not made here.
//
// There is ONE endpoint behind this page and it returns one of two different
// payloads depending on who is asking. A reader with access gets the blog. A
// reader without it gets a preview that has no body, no image URLs and no
// video URL in it at all. So a shared link cannot leak a paid blog, and there
// is nothing in the response for a curious reader to dig out of devtools —
// the bypass is not blocked, it is absent.
//
// THIS PAGE IS PUBLIC ON PURPOSE. A share link has to open for someone who is
// not signed in; they see the preview and are asked to sign in and pay. Making
// the route private would bounce them to a login screen with no idea what they
// were invited to read.
// =====================================================

function ProBlogView() {
  const { blogId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState(null);

  const username = localStorage.getItem("username") || "";
  const userPic = localStorage.getItem("profilePic") || "";

  const flash = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3800);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getProBlog(blogId);
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "This Pro blog could not be opened");
    } finally {
      setLoading(false);
    }
  }, [blogId]);

  useEffect(() => {
    load();
  }, [load]);

  const unlock = useCallback(() => {
    // A signed-out visitor cannot pay: there is no account to attach the access
    // to. Send them to sign in first and bring them straight back here.
    if (!data?.authenticated) {
      navigate(`/?next=${encodeURIComponent(`/pro-blog/${blogId}`)}`);
      return;
    }

    if (paying || !data?.creator) return;
    setPaying(true);

    payForCreatorAccess({
      creatorId: data.creator._id,
      creatorName: data.creator.name,
      userName: username,

      onSuccess: async (res) => {
        setPaying(false);
        flash(res.message || "Unlocked!", "success");
        await load();
      },

      onError: (msg) => {
        setPaying(false);
        flash(msg, "error");
      },

      onClose: () => setPaying(false),
    });
  }, [data, paying, blogId, username, navigate, flash, load]);

  if (loading) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-loading-screen">
          <div className="pro-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !data?.blog) {
    return (
      <div className="pro-page-wrap">
        <Navbar />
        <div className="pro-container">
          <div className="pro-card pro-empty">
            <p>{error || "Pro blog not found"}</p>
            <button type="button" className="pro-btn pro-btn-ghost" onClick={() => navigate(-1)}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { blog, creator, myAccess, unlockPriceRupees, authenticated } = data;

  return (
    <div className="pro-page-wrap">
      <Navbar />

      {toast && <div className={`pro-toast pro-toast-${toast.type}`}>{toast.msg}</div>}

      <div className="pro-container pro-container-narrow">
        {/* ── CREATOR STRIP — also the way back to the rest of their blogs ── */}
        {creator && (
          <section className="pro-card pro-creator-strip">
            <button
              type="button"
              className="pro-creator-strip-main"
              onClick={() => navigate(`/pro-creator/${creator._id}`)}
            >
              {creator.profilePic ? (
                <img src={creator.profilePic} alt={creator.name} className="pro-strip-avatar" />
              ) : (
                <span className="pro-strip-avatar pro-blog-avatar-fallback">
                  {(creator.name || "?").charAt(0).toUpperCase()}
                </span>
              )}

              <span className="pro-strip-meta">
                <strong>{creator.name}</strong>
                <small>
                  💎 Pro Creator · {creator.paidUserCount} paid user
                  {creator.paidUserCount === 1 ? "" : "s"}
                </small>
              </span>
            </button>

            {myAccess?.status === "active" && (
              <span className="pro-chip pro-chip-live">
                🟢 {formatRemaining(myAccess.remainingMs)} left
              </span>
            )}
          </section>
        )}

        {/* ── THE BLOG ──
            Title → Content → Image slider → Video, or the locked preview.
            ProBlogCard renders whichever shape the server sent. */}
        <ProBlogCard
          blog={blog}
          currentUsername={username}
          currentUserPic={userPic}
          unlockPriceRupees={unlockPriceRupees}
          onUnlock={blog.locked ? unlock : null}
        />

        {/* ── THE PAY STEP OF STEP 14 ── */}
        {blog.locked && (
          <section className="pro-card pro-access-bar">
            <div className="pro-access-state">
              <h2 className="pro-access-title">
                {authenticated ? "Unlock to keep reading" : "Sign in to unlock"}
              </h2>
              <p className="pro-access-note">
                {authenticated ? (
                  <>
                    ₹{unlockPriceRupees} unlocks every Pro blog by{" "}
                    <strong>{creator?.name || "this creator"}</strong> for 30 days. It
                    applies to this creator only.
                  </>
                ) : (
                  <>
                    You need a TheBlogsSphere account to unlock this creator&rsquo;s Pro
                    blogs.
                  </>
                )}
              </p>
              {myAccess?.status === "expired" && (
                <p className="pro-access-note pro-access-note-expired">
                  Your access expired on {formatDate(myAccess.expiryDate)}.
                </p>
              )}
            </div>

            <button
              type="button"
              className="pro-btn pro-btn-primary pro-btn-lg"
              onClick={unlock}
              disabled={paying}
            >
              {paying
                ? "Opening payment…"
                : authenticated
                ? `🔓 Pay ₹${unlockPriceRupees}`
                : "Sign in"}
            </button>
          </section>
        )}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

export default ProBlogView;
